import {
  HeadingProvider,
  Inline,
  SkeletonProvider,
  Stack,
  StyleProp,
  Tiles,
  Text,
  Skeleton
} from '@front10/base-elements';
import * as React from 'react';
import { useInfiniteQuery } from 'react-query';
import Button from '../../../packages/base-elements/Button2';
import { isSSR } from '@front10/utils';
import EventCard from '../../items/EventCard';
import { ContactButton } from './ContactButton';
import { Disclaimer } from './Disclaimer';
import {
  EventsDataFetchFn,
  fetchEvents,
  usePaginatedEventsDataMapper,
  usePaginatedPlaceholderEvents
} from './eventsData';
import { Filters, useFiltersState } from './Filters';
import { Heading } from './Heading';
import { MoreInfoButton } from './MoreInfoButton';
import { NoResultsAlert } from './NoResultsAlert';
import TicketEventsProvider, { TicketEventsProviderProps } from './TicketEventsProvider';
import { ComponentsConfiguration } from './types';
import { useTranslation } from '@front10/utils';
import { validatePrepopulationData } from './util.js';

interface Props extends TicketEventsProviderProps {
  componentsConfiguration?: ComponentsConfiguration;
  fetchData?: EventsDataFetchFn;
  styles?: StyleProp;
  integrationSettings: IntegrationSettings;
  showHeading?: boolean;
  showDisclaimer?: boolean;
  headerSettings?: {
    showTitle?: boolean;
    showSubtitle?: boolean;
  };
  prePopulation?: {};
  queryId?: string;
}

export default function PremiumExperienceGrid({
  componentsConfiguration = {},
  fetchData = fetchEvents,
  queryId = 'ticket-events',
  styles,
  integrationSettings = {},
  showHeading,
  showDisclaimer = true,
  headerSettings = {
    showTitle: true,
    showSubtitle: false
  },
  locale,
  headingLevelStart = 1,
  prePopulation
}: Props) {
  return (
    <TicketEventsProvider
      headingLevelStart={headingLevelStart}
      locale={locale}
      filters={componentsConfiguration.filterContainer}
      prePopulation={validatePrepopulationData({
        prePopulation: prePopulation ?? {},
        componentsConfiguration
      })}
    >
      <Stack variant="layout.componentRoot" sx={styles} gap="md">
        {showHeading ? (
          <Heading componentName="premium-experience-grid" {...headerSettings} />
        ) : null}
        <Stack data-container-body gap="md">
          <Filters componentName="premium-experience-grid" />
          <HeadingProvider>
            <DataPanel
              componentName="premium-experience-grid"
              queryId={queryId}
              fetchData={fetchData}
              integrationSettings={integrationSettings['ticketEvents']}
              componentsConfiguration={componentsConfiguration}
            />
            {showDisclaimer && <Disclaimer componentName="premium-experience-grid" />}
          </HeadingProvider>
        </Stack>
      </Stack>
    </TicketEventsProvider>
  );
}

type DataPanelProps = Required<Pick<Props, 'fetchData' | 'queryId' | 'componentsConfiguration'>> & {
  integrationSettings?: Record<string, any>;
  componentName?: string;
};

function DataPanel({
  fetchData,
  integrationSettings,
  queryId,
  componentsConfiguration,
  componentName = 'premium-experience-grid'
}: DataPanelProps) {
  const translate = useTranslation();
  const state = useFiltersState((state) => state.state);
  const context = useFiltersState((state) => state.context);
  const ticketOffersOption = useFiltersState((state) => state.ticketOffersOption);
  const filters = { offerType: context, ...state[context] };
  const onSearch = useFiltersState((state) => state.onSearch);
  const numberOfCardsPerRow = componentsConfiguration?.gridSettings?.numberOfCardsPerRow ?? 4;

  const getColumnsStructure = (value: number) => {
    if (value && value >= 2 && value <= 4) {
      return Array.from({ length: value }, (_, i) => i + 1);
    }
    return [1, 2, 3, 4];
  };

  const columnsStructure = componentsConfiguration?.gridSettings?.numberOfCardsPerRow
    ? getColumnsStructure(numberOfCardsPerRow)
    : [1, 2, 3, 4];

  const placeholderData = usePaginatedPlaceholderEvents();
  const paginatedEventsDataMapper = usePaginatedEventsDataMapper();
  const {
    // status,
    data,
    // error,
    isPlaceholderData,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery(
    [queryId, context, filters, integrationSettings],
    ({ pageParam = 0 }) => {
      return fetchData({
        pageSize: 12,
        ...integrationSettings,
        page: pageParam,
        filters,
        ticketOffersOption
      });
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.data?.length ? lastPage.pagination.next ?? false : false,
      placeholderData,
      select: paginatedEventsDataMapper
    }
  );

  const dataLength = React.useMemo(() => {
    let length = 0;
    data?.pages?.forEach((page) => {
      length += page.data.length;
    });
    return length;
  }, [data]);

  const allDataLength = React.useMemo(() => {
    return data?.pages[0]?.pagination?.allDataLength;
  }, [data]);

  let content = null;
  let cardIndex = 0;

  if (!data?.pages[0].data.length) {
    content = <NoResultsAlert />;
  } else {
    const showPill = componentsConfiguration.card?.showPill ?? false;
    const moreInfoLinkInteractionType =
      componentsConfiguration.card?.moreInfoLinkInteractionType ?? 'popup';
    const contactEmail = componentsConfiguration?.contactEmail;
    const moreInfoTarget = componentsConfiguration?.card?.moreInfoLinkTarget ?? '_blank';
    const ctaTarget = componentsConfiguration?.card?.moreInfoLinkTarget ?? '_blank';
    const cardSettings = componentsConfiguration?.card
      ? {
          ...componentsConfiguration?.card
        }
      : {};
    content =
      data?.pages.map((page, pageIndex) => {
        cardIndex += data.pages[pageIndex - 1]?.data.length || 0;
        return (
          <React.Fragment key={page.pagination.next?.toString() ?? pageIndex}>
            <Tiles
              columns={columnsStructure}
              gap={['1', '1', '2', 'lg']}
              // Fix for Safari bug stretching the cards
              sx={{ gridAutoRows: '-webkit-max-content' }}
            >
              {page.data.map((cardData, index) => {
                const ctaSlot = cardData.ctaUrl ? (
                  <Button
                    variant="primary"
                    // href={cardData.ctaUrl}
                    onClick={() => {
                      if (!isSSR()) {
                        window.open(cardData.ctaUrl, ctaTarget);
                      }
                      onSearch(cardData, filters);
                    }}
                  >
                    {cardData.ctaLabel}
                  </Button>
                ) : (
                  <ContactButton
                    data={cardData}
                    onClick={() => onSearch(cardData, filters)}
                    componentName="premium-experience-grid"
                    contactEmail={contactEmail}
                  />
                );
                return (
                  <EventCard
                    key={index}
                    {...cardData}
                    counter={showPill ? `${cardIndex + index + 1}/${dataLength}` : undefined}
                    moreInfoSlot={
                      cardData.offerLabel?.toLowerCase() !== 'ticket only' ? (
                        <MoreInfoButton
                          data={{
                            ...cardData,
                            ctaSlot: React.cloneElement(ctaSlot, { autoFocus: true }),
                            moreInfoLinkInteractionType
                          }}
                          target={moreInfoTarget}
                        />
                      ) : undefined
                    }
                    ctaSlot={ctaSlot}
                    cardSettings={cardSettings}
                    componentName={componentName}
                  />
                );
              })}
            </Tiles>
          </React.Fragment>
        );
      }) ?? null;
  }

  return (
    <SkeletonProvider isLoading={isPlaceholderData}>
      {componentsConfiguration.showCounter ? (
        <Skeleton
          variant="rectangular"
          width="20%"
          height="35px"
          style={{ marginTop: '-37px', marginLeft: 'auto', width: '20%' }}
        >
          {allDataLength && dataLength ? (
            <Inline data-counter={dataLength} align="right">
              <Text variant="text.fontSize5">
                {translate(
                  'cmp-premium-experience-counter',
                  '{dataLength} OF {allDataLength} results showing',
                  { dataLength, allDataLength }
                )}
              </Text>
            </Inline>
          ) : null}
        </Skeleton>
      ) : null}
      {content}
      {hasNextPage ? (
        <Inline align="center" p="4">
          <Button
            variant="secondary"
            sx={{ width: '160px' }}
            onPress={() => {
              if (!isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage
              ? translate('cmp-premium-experience-grid-loading', 'Loading...')
              : translate('cmp-premium-experience-grid-loadmore', 'Load More')}
          </Button>
        </Inline>
      ) : null}
    </SkeletonProvider>
  );
}
